import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Search } from 'lucide-react';
import type { HydratedArticleItem } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  groupContentErrorMessage,
  groupContentsArticlePickerQueryOptions,
} from '@/lib/group-contents';
import { categoriesListQueryOptions } from '@/lib/taxonomies';

interface ArticlePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alreadyPicked?: string[];
  onPick: (article: HydratedArticleItem) => void;
  /** When true, the picker server-side filters to `is_press_release=1` —
   *  PR override flow uses this so admins can't accidentally pick a
   *  non-PR article as the source. */
  pressReleaseOnly?: boolean;
}

export function ArticlePickerDialog({
  open,
  onOpenChange,
  alreadyPicked,
  onPick,
  pressReleaseOnly = false,
}: ArticlePickerDialogProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [categorySlug, setCategorySlug] = useState<string | null>(null);
  const [sort, setSort] = useState<'latest' | 'popular'>('latest');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    if (open) {
      setQuery('');
      setCategorySlug(null);
      setSort('latest');
    }
  }, [open]);

  const categoriesQuery = useQuery({
    ...categoriesListQueryOptions(),
    enabled: open,
    staleTime: 60_000,
  });
  const categories = categoriesQuery.data ?? [];

  const pickerQuery = useQuery({
    ...groupContentsArticlePickerQueryOptions({
      q: debounced,
      categorySlug,
      sort,
      pressReleaseOnly,
    }),
    enabled: open,
  });

  const items = pickerQuery.data ?? [];
  const pickedSet = new Set(alreadyPicked ?? []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{pressReleaseOnly ? 'เลือกข่าวประชาสัมพันธ์' : 'เลือกบทความ'}</DialogTitle>
          <DialogDescription>
            {pressReleaseOnly
              ? 'แสดงเฉพาะบทความที่ตั้งค่า "ข่าวประชาสัมพันธ์" ไว้ใน /posts'
              : 'ค้นหาจากชื่อบทความ (เฉพาะที่เผยแพร่แล้ว)'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="พิมพ์ชื่อบทความ..."
                autoFocus
                className="pl-9"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <select
              value={categorySlug ?? ''}
              onChange={(e) => setCategorySlug(e.target.value || null)}
              className="border-input bg-background h-10 rounded-md border px-3 text-sm"
              title="กรองตามหมวด"
            >
              <option value="">ทุกหมวด</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as 'latest' | 'popular')}
              className="border-input bg-background h-10 rounded-md border px-3 text-sm"
              title="เรียงตาม"
            >
              <option value="latest">ล่าสุด</option>
              <option value="popular">ยอดนิยม</option>
            </select>
          </div>

          {pickerQuery.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{groupContentErrorMessage(pickerQuery.error)}</AlertDescription>
            </Alert>
          )}

          <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800">
            {pickerQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">กำลังโหลด...</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                {debounced ? `ไม่พบบทความ "${debounced}"` : 'ยังไม่มีบทความที่เผยแพร่'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((p) => {
                  const isDup = pickedSet.has(p.id);
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        disabled={isDup}
                        onClick={() => {
                          onPick(p);
                          onOpenChange(false);
                        }}
                        className={`flex w-full items-start gap-3 p-3 text-left transition ${
                          isDup
                            ? 'cursor-not-allowed opacity-40'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        {p.coverImageUrl ? (
                          <img
                            src={p.coverImageUrl}
                            alt=""
                            loading="lazy"
                            className="h-12 w-20 shrink-0 rounded object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.visibility = 'hidden';
                            }}
                          />
                        ) : (
                          <div className="h-12 w-20 shrink-0 rounded bg-slate-100 dark:bg-slate-800" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
                            {p.title}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                            {p.publishedAt && (
                              <span>
                                {new Date(p.publishedAt).toLocaleDateString('th-TH', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: '2-digit',
                                })}
                              </span>
                            )}
                            {p.categoryName && (
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                {p.categoryName}
                              </span>
                            )}
                            {isDup && (
                              <span className="text-amber-600 dark:text-amber-400">
                                · อยู่ใน group นี้แล้ว
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ปิด
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
